"use client";

import { Select as SelectPrimitive } from "@base-ui/react/select";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import * as React from "react";

import { cn } from "../lib/utils";

// Radix's SelectValue rendered the selected item's CONTENT; Base UI renders
// the raw value string unless the root is given item labels. Walk the wrapper
// children for SelectItems and derive that map so triggers keep showing
// labels without every call site passing `items` explicitly.
//
// The walk sees JSX, not rendered output: a caller-defined component that
// renders the SelectItems itself is opaque here, and the trigger would show the
// raw value. Such callers pass `items` explicitly; in development the wrapper
// warns when it meets that shape so the fallback can't ship silently.
function collectItemLabels(
  node: React.ReactNode,
  labels: { value: unknown; label: React.ReactNode }[],
  seen: { opaqueComponent: boolean },
) {
  React.Children.forEach(node, (child) => {
    if (!React.isValidElement(child)) {
      return;
    }

    if (child.type === SelectItem) {
      const { value, children } = child.props as SelectPrimitive.Item.Props;
      labels.push({ value, label: children });
      return;
    }

    const children = (child.props as { children?: React.ReactNode }).children;

    if (children != null) {
      collectItemLabels(children, labels, seen);
      return;
    }

    if (
      typeof child.type !== "string" &&
      child.type !== React.Fragment &&
      !SELECT_PARTS.has(child.type)
    ) {
      seen.opaqueComponent = true;
    }
  });
}

function Select<Value>({
  items,
  children,
  ...props
}: SelectPrimitive.Root.Props<Value>) {
  const derived = React.useMemo(() => {
    if (items != null) {
      return { items, opaqueComponent: false };
    }

    const labels: { value: unknown; label: React.ReactNode }[] = [];
    const seen = { opaqueComponent: false };
    collectItemLabels(children, labels, seen);
    return {
      items: labels.length > 0 ? labels : undefined,
      opaqueComponent: seen.opaqueComponent,
    };
  }, [items, children]);

  const warnedRef = React.useRef(false);

  if (
    process.env.NODE_ENV !== "production" &&
    !warnedRef.current &&
    derived.items == null &&
    derived.opaqueComponent &&
    (props.value != null || props.defaultValue != null)
  ) {
    warnedRef.current = true;
    console.warn(
      "[@sfc/ui] Select: its items are rendered by a component the wrapper " +
        "cannot see into, so the trigger will show the raw value instead of " +
        "the item label. Pass `items` to <Select> explicitly.",
    );
  }

  return (
    <SelectPrimitive.Root
      items={derived.items as SelectPrimitive.Root.Props<Value>["items"]}
      {...props}
    >
      {children}
    </SelectPrimitive.Root>
  );
}

// The wrapper's own parts never hide items behind a render of their own;
// only caller-defined components do. (Function declarations hoist.)
const SELECT_PARTS: ReadonlySet<unknown> = new Set<unknown>([
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
]);

function SelectGroup({ ...props }: SelectPrimitive.Group.Props) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

function SelectValue({ ...props }: SelectPrimitive.Value.Props) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

function SelectTrigger({
  className,
  size = "default",
  children,
  ...props
}: SelectPrimitive.Trigger.Props & {
  size?: "sm" | "default";
}) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      className={cn(
        "border-input data-placeholder:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 hover:border-border-strong dark:hover:bg-input/50 flex w-fit items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm whitespace-nowrap inset-shadow-input-rest active:inset-shadow-input-pressed transition-[color,box-shadow,border-color] duration-150 ease-out outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 cursor-pointer",
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon render={<ChevronDownIcon className="size-4" />} />
    </SelectPrimitive.Trigger>
  );
}

function SelectContent({
  className,
  children,
  side = "bottom",
  sideOffset = 4,
  align = "center",
  alignOffset = 0,
  alignItemWithTrigger = false,
  ...props
}: SelectPrimitive.Popup.Props &
  Pick<
    SelectPrimitive.Positioner.Props,
    "side" | "sideOffset" | "align" | "alignOffset" | "alignItemWithTrigger"
  >) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        alignItemWithTrigger={alignItemWithTrigger}
        className="isolate z-50"
      >
        <SelectPrimitive.Popup
          data-slot="select-content"
          className={cn(
            "bg-surface-elevated text-text-primary border-border-subtle relative z-50 max-h-(--available-height) min-w-32 origin-(--transform-origin) overflow-x-hidden overflow-y-auto rounded-md border shadow-md transition-[transform,opacity] duration-150 data-starting-style:scale-95 data-starting-style:opacity-0 data-ending-style:scale-95 data-ending-style:opacity-0",
            className,
          )}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.List className="w-full min-w-(--anchor-width) scroll-my-1 p-1.5">
            {children}
          </SelectPrimitive.List>
          <SelectScrollDownButton />
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  );
}

function SelectLabel({
  className,
  ...props
}: SelectPrimitive.GroupLabel.Props) {
  return (
    <SelectPrimitive.GroupLabel
      data-slot="select-label"
      className={cn("text-muted-foreground px-2 py-1.5 text-xs", className)}
      {...props}
    />
  );
}

function SelectItem({
  className,
  children,
  ...props
}: SelectPrimitive.Item.Props) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "text-text-primary relative flex w-full cursor-default items-center gap-2 rounded-md py-2 pr-8 pl-3 text-sm outline-hidden select-none data-highlighted:bg-surface-hover [&_svg:not([class*='text-'])]:text-text-muted data-highlighted:[&_svg]:text-text-primary data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        // Base UI marks the selected item with an empty data-selected, which
        // shadcn's data-selected variant (data-selected="true" only) misses.
        "data-[selected]:bg-surface-hover data-[selected]:[&_svg]:text-text-primary",
        className,
      )}
      {...props}
    >
      <span
        data-slot="select-item-indicator"
        className="absolute right-2 flex size-3.5 items-center justify-center"
      >
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({
  className,
  ...props
}: SelectPrimitive.Separator.Props) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn("bg-border pointer-events-none -mx-1 my-1 h-px", className)}
      {...props}
    />
  );
}

function SelectScrollUpButton({
  className,
  ...props
}: SelectPrimitive.ScrollUpArrow.Props) {
  return (
    <SelectPrimitive.ScrollUpArrow
      data-slot="select-scroll-up-button"
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className,
      )}
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpArrow>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: SelectPrimitive.ScrollDownArrow.Props) {
  return (
    <SelectPrimitive.ScrollDownArrow
      data-slot="select-scroll-down-button"
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className,
      )}
      {...props}
    >
      <ChevronDownIcon className="size-4" />
    </SelectPrimitive.ScrollDownArrow>
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
